/**
 * AI Search Portal - Premium Agent Landing Page
 * Navy + Gold design matching marketing flyers
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faChartLine,
  faCheckCircle,
  faArrowRight,
  faHome,
  faRobot,
  faUserTie,
  faShieldAlt,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const isKairosIQDomain = typeof window !== 'undefined' && (
  window.location.hostname.includes('app.kairosiq.ai') ||
  window.location.hostname.includes('demo.kairosiq.ai') ||
  window.location.hostname.includes('dev.app.kairosiq.ai') ||
  window.location.search.includes('brand=kairosiq')
);

const AgentPortalLanding: React.FC = () => {
  const navigate = useNavigate();
  const [typingText, setTypingText] = useState('');
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

  const prompts = [
    "Find 3 bedroom homes in Scottsdale under $800k",
    "Show me pool homes in the West Valley",
    "What's my home worth in today's market?",
    "Modern condo in Tempe with mountain views",
    "Single story homes in Gilbert with a big yard"
  ];

  useEffect(() => {
    const currentPrompt = prompts[currentPromptIndex];
    let charIndex = 0;
    let isTyping = true;

    const typeInterval = setInterval(() => {
      if (isTyping && charIndex <= currentPrompt.length) {
        setTypingText(currentPrompt.slice(0, charIndex));
        charIndex++;
      } else if (isTyping) {
        setTimeout(() => { isTyping = false; }, 2000);
      } else {
        setTypingText('');
        setCurrentPromptIndex((prev) => (prev + 1) % prompts.length);
        clearInterval(typeInterval);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, [currentPromptIndex]);

  // Inline styles using flyer design system
  const colors = {
    navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
    gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
    white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF'
  };

  return (
    <div style={{ background: colors.navy, color: colors.white, fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: '100vh' }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Navigation */}
      <nav style={{ background: `linear-gradient(135deg, ${colors.navy3} 0%, #0d1e38 100%)`, borderBottom: `2px solid ${colors.gold}`, padding: '16px 0' }}>
        <Container className="d-flex justify-content-between align-items-center">
          <div>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px' }}>
              {isKairosIQDomain ? (
                <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span></>
              ) : (
                <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '18px' }}>.com</span></>
              )}
            </span>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <a href="/pricing" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Pricing</a>
            <a href="/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Events</a>
            <a href="/request-demo" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Book Demo</a>
            <a href="/broker" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>For Brokers</a>
            <Button
              variant="outline-light"
              size="sm"
              style={{ borderColor: colors.muted, color: colors.muted, fontSize: '13px' }}
              onClick={() => navigate('/agent-portal/login')}
            >
              Agent Login
            </Button>
            <Button
              size="sm"
              style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, fontSize: '13px', letterSpacing: '0.5px' }}
              onClick={() => navigate('/agent-portal/signup')}
            >
              Get Started
            </Button>
          </div>
        </Container>
      </nav>

      {/* Stats Strip */}
      <div style={{ background: colors.gold, padding: '14px 0' }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center text-center">
            {[
              { num: '1.6M+', label: 'Closed Transactions Trained' },
              { num: '197', label: 'Zip-Code AI Models' },
              { num: '91.79%', label: 'Valuation Accuracy' },
              { num: '35 sec', label: 'Full AI CMA Report' },
              { num: '24/7', label: 'AI Working for Your Buyers' }
            ].map((stat, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div style={{ width: '1px', height: '34px', background: 'rgba(15,30,53,0.3)' }} />}
                <div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '26px', color: colors.navy, lineHeight: 1, letterSpacing: '1px' }}>{stat.num}</div>
                  <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: colors.navy2, marginTop: '2px' }}>{stat.label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </Container>
      </div>

      {/* Hero */}
      <section style={{ padding: '50px 0 40px' }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={7}>
              <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '3px', textTransform: 'uppercase', color: colors.gold, marginBottom: '12px' }}>
                Built by Arizona Licensed Brokers · Powered by KairosIQ AI
              </p>
              <h1 style={{ fontSize: '44px', lineHeight: 1.15, marginBottom: '16px', color: colors.white, fontWeight: 700 }}>
                Stop using tools.<br /><span style={{ color: colors.gold2 }}>Start deploying intelligence.</span>
              </h1>
              <p style={{ fontSize: '17px', color: colors.off, lineHeight: 1.7, maxWidth: '560px', marginBottom: '30px' }}>
                You have enough tools. What you need is an AI teammate that searches the MLS, engages your buyers 24/7, and generates seller CMAs in 35 seconds — while you focus on relationships, negotiation, and closing.
              </p>

              {/* Typing Demo */}
              <div style={{ background: colors.navy2, borderRadius: '8px', padding: '16px 20px', marginBottom: '30px', borderLeft: `3px solid ${colors.gold}` }}>
                <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: colors.gold, marginBottom: '8px' }}>
                  Your Buyer's AI Search
                </div>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faComments} style={{ color: colors.gold2, marginRight: '12px', fontSize: '18px' }} />
                  <span style={{ fontSize: '16px', color: colors.off }}>{typingText}<span style={{ color: colors.gold, animation: 'blink 1s infinite' }}>|</span></span>
                </div>
              </div>

              <div className="d-flex gap-3 align-items-center">
                <Button
                  size="lg"
                  style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, padding: '14px 28px', fontSize: '15px', letterSpacing: '0.5px' }}
                  onClick={() => navigate('/agent-portal/signup')}
                >
                  Start Today — Plans from $70/month <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </Button>
                <div style={{ fontSize: '12px', color: colors.muted }}>
                  No contract · Cancel anytime
                </div>
              </div>
            </Col>

            <Col lg={5}>
              {/* NAR Narrative Box */}
              <div style={{ background: colors.navy2, borderLeft: `4px solid ${colors.gold}`, borderRadius: '0 8px 8px 0', padding: '20px', marginBottom: '20px' }}>
                <div style={{ background: 'rgba(139,26,26,0.2)', borderRadius: '6px', padding: '14px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#e06060', marginBottom: '6px' }}>Without {isKairosIQDomain ? 'KairosIQ' : 'AI Search Portal'}</div>
                  <div style={{ fontSize: '14px', color: '#e8c0c0', lineHeight: 1.5 }}>
                    You spend hours searching MLS, pulling comps, and chasing leads. Your clients wait. Your competitors move faster.
                  </div>
                </div>
                <div style={{ textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', color: colors.gold, margin: '8px 0' }}>↓</div>
                <div style={{ background: 'rgba(26,107,60,0.2)', borderRadius: '6px', padding: '14px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#60c090', marginBottom: '6px' }}>With {isKairosIQDomain ? 'KairosIQ' : 'AI Search Portal'}</div>
                  <div style={{ fontSize: '14px', color: '#b8e8d0', lineHeight: 1.5, fontWeight: 500 }}>
                    AI does the searching. AI does the comps. You do what earns the commission — advise, negotiate, and close.
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 5 Capabilities */}
      <section style={{ padding: '40px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, letterSpacing: '2px', marginBottom: '24px' }}>
            5 Ways It Transforms Your Practice
          </h2>
          <Row className="g-3">
            {[
              { num: '1', title: 'Your Buyer Has AI Working 24/7', desc: 'Private portal, natural language + voice search. Wake up to a notification showing which properties they saved — and why.' },
              { num: '2', title: 'AI That Knows This Market', desc: 'Trained on closed transactions — school districts, neighborhood appreciation, the gap between what buyers searched and what they bought.' },
              { num: '3', title: 'Seller CMAs in 35 Seconds', desc: '15 comps, 126 property features analyzed, Fannie Mae condition adjustments built in. Intelligence your competitors spent hours pulling manually.' },
              { num: '4', title: 'Explainable AI Your Clients Trust', desc: 'Every valuation shows exactly which features drove the number. Your sellers can chat with the AI about specific comps. Full transparency.' },
            ].map((cap, i) => (
              <Col md={6} key={i}>
                <div style={{ background: colors.navy2, borderRadius: '8px', padding: '22px 24px', display: 'flex', gap: '16px', alignItems: 'flex-start', height: '100%' }}>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, lineHeight: 1, flexShrink: 0 }}>{cap.num}</div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: colors.white, marginBottom: '6px' }}>{cap.title}</div>
                    <div style={{ fontSize: '15px', color: colors.muted, lineHeight: 1.6 }}>{cap.desc}</div>
                  </div>
                </div>
              </Col>
            ))}
            <Col xs={12}>
              <div style={{ background: colors.navy2, borderRadius: '8px', padding: '22px 24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, lineHeight: 1, flexShrink: 0 }}>5</div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: colors.white, marginBottom: '6px' }}>You Focus on Relationships — Not Repetitive Tasks</div>
                  <div style={{ fontSize: '15px', color: colors.muted, lineHeight: 1.6 }}>AI handles search, CMA prep, and buyer engagement. You negotiate, advise, advocate, and close. Agentic AI doesn't replace your value — it proves it.</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Pricing Strip */}
      <section style={{ padding: '40px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, letterSpacing: '2px', marginBottom: '24px' }}>
            Choose Your Plan
          </h2>
          <Row className="g-3">
            {[
              { name: 'Agent CRM', price: '$70', freq: '/month', featured: false, coming: false, features: ['Client Hub — manage all leads', 'Email from your Gmail/Outlook', 'MLS natural language search', 'AI home valuation with comps', 'Your branding · Fair Housing compliant'] },
              { name: 'AI Search  ★', price: '$135', freq: '/month', featured: true, coming: false, features: ['Everything in Agent CRM', 'AI buyer portal for your clients', 'Smart CMA with AI analysis', 'Shareable seller reports', 'Real-time email sync'] },
              { name: 'AI Pro', price: '$185', freq: '/month', featured: false, coming: false, features: ['Everything in AI Search', 'AI Lead Coach & action plans', 'Instant AI response to new leads', 'Monthly seller CMA auto-updates', 'AI-drafted follow-ups'] },
              { name: 'Broker Portal', price: '$500', freq: '/month', featured: false, coming: false, features: ['Agent CRM seats included', 'Agents upgrade tiers individually', 'AI document review', 'Broker compliance dashboard', 'Ask the Broker AI chatbot'] },
            ].map((plan: any, i: number) => (
              <Col md={3} key={i}>
                <div style={{
                  background: colors.navy2,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: plan.featured ? `2px solid ${colors.gold}` : '1px solid rgba(201,149,42,0.15)',
                  boxShadow: plan.featured ? '0 0 20px rgba(201,149,42,0.15)' : 'none',
                  height: '100%'
                }}>
                  <div style={{
                    background: plan.featured ? colors.gold : colors.navy3,
                    padding: '14px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    position: 'relative' as const
                  }}>
                    {plan.coming && (
                      <span style={{ position: 'absolute' as const, top: '8px', right: '8px', background: plan.featured ? 'rgba(15,30,53,0.2)' : 'rgba(201,149,42,0.15)', color: plan.featured ? colors.navy : colors.gold2, fontSize: '9px', fontWeight: 700, letterSpacing: '1px', padding: '3px 8px', borderRadius: '3px' }}>
                        {plan.comingLabel}
                      </span>
                    )}
                    <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: plan.featured ? colors.navy : colors.muted, marginBottom: '4px' }}>
                      {plan.name}
                    </div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '34px', color: plan.featured ? colors.navy : colors.white, lineHeight: 1 }}>
                      {plan.price}<span style={{ fontSize: '14px', color: plan.featured ? colors.navy2 : colors.muted, marginLeft: '3px' }}>{plan.freq}</span>
                    </div>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    {plan.features.map((f: string, fi: number) => (
                      <div key={fi} style={{ fontSize: '14px', color: colors.off, padding: '4px 0', display: 'flex', gap: '8px', alignItems: 'baseline', lineHeight: 1.4 }}>
                        <span style={{ color: colors.gold2, fontSize: '13px', fontWeight: 700 }}>✓</span>
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-4">
            <Button
              size="lg"
              style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, padding: '14px 40px', fontSize: '15px', letterSpacing: '1px' }}
              onClick={() => navigate('/pricing')}
            >
              View Full Pricing <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* Moat Section */}
      <section style={{ padding: '30px 0' }}>
        <Container>
          <div style={{
            background: 'linear-gradient(135deg, rgba(201,149,42,0.12) 0%, rgba(30,53,87,0.4) 100%)',
            border: `1px solid rgba(201,149,42,0.3)`,
            borderRadius: '8px',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
          }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: colors.gold, flexShrink: 0, lineHeight: 1, writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', letterSpacing: '3px' }}>
              MOAT
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: colors.white, marginBottom: '8px' }}>The Intelligence Nobody Else Has</div>
              <div style={{ fontSize: '15px', color: colors.muted, lineHeight: 1.7 }}>
                Not listing descriptions. Not national averages. 1.66 million real Phoenix buyer behaviors — what they searched for, and what they actually closed on. 197 zip-code-specific AI models built from actual closed sales data. No other platform has this level of local market intelligence.
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Market Expansion */}
      <section style={{ padding: '40px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, letterSpacing: '2px', marginBottom: '8px' }}>
            Markets We Serve
          </h2>
          <p style={{ fontSize: '15px', color: colors.muted, marginBottom: '24px' }}>
            Expanding across the Sun Belt with locally-trained AI models for each market.
          </p>
          <Row className="g-3">
            {[
              { market: 'Phoenix, AZ', status: 'LIVE', detail: '1.66M transactions · 197 zip models · ARMLS', color: '#1A6B3C', bg: 'rgba(26,107,60,0.15)' },
              { market: 'Tampa, FL', status: 'MAY 2026', detail: 'Stellar MLS · 70,000+ members · Bridge API', color: colors.gold, bg: 'rgba(201,149,42,0.1)' },
              { market: 'Houston, TX', status: 'Q3 2026', detail: 'HAR MLS · 36,000+ agents · Bridge API', color: colors.muted, bg: 'rgba(143,163,191,0.1)' },
              { market: 'Tucson, AZ', status: 'Q3 2026', detail: 'Tucson MLS · Expanding Arizona coverage', color: colors.muted, bg: 'rgba(143,163,191,0.1)' },
            ].map((m, i) => (
              <Col md={3} key={i}>
                <div style={{ background: colors.navy2, borderRadius: '8px', padding: '20px', height: '100%', borderLeft: `3px solid ${m.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: colors.white }}>{m.market}</div>
                    <span style={{ background: m.bg, color: m.color, fontSize: '10px', fontWeight: 700, letterSpacing: '1px', padding: '3px 8px', borderRadius: '3px' }}>
                      {m.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: colors.muted, lineHeight: 1.5 }}>{m.detail}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Video Section Placeholder */}
      <section style={{ padding: '40px 0' }}>
        <Container className="text-center">
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, letterSpacing: '2px', marginBottom: '16px' }}>
            See It In Action
          </h2>
          <div style={{
            background: colors.navy2,
            borderRadius: '12px',
            padding: '60px',
            border: `1px solid rgba(201,149,42,0.2)`,
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>▶️</div>
            <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: colors.white, marginBottom: '8px' }}>Product Demo Coming Soon</h3>
            <p style={{ color: colors.muted, fontSize: '14px' }}>Watch how agents use {isKairosIQDomain ? 'KairosIQ' : 'AI Search Portal'} to win more listings and serve buyers better.</p>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section style={{ background: `linear-gradient(90deg, ${colors.navy3} 0%, #0d1e38 100%)`, borderTop: `3px solid ${colors.gold}`, padding: '30px 0' }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', color: colors.gold2, letterSpacing: '1px' }}>{isKairosIQDomain ? 'KAIROSIQ' : 'aisearchportal.com'}</div>
              <div style={{ fontSize: '12px', color: colors.muted, marginTop: '4px' }}>support@kairosiq.ai · (520) 251-9595 · KairosIQ AI, Inc. · Chandler, Arizona</div>
            </div>
            <Button
              style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, fontSize: '14px', letterSpacing: '1px', textTransform: 'uppercase', padding: '12px 24px' }}
              onClick={() => navigate('/agent-portal/signup')}
            >
              Sign Up Today
              <div style={{ fontSize: '10px', fontWeight: 400, letterSpacing: '0.5px', marginTop: '2px', opacity: 0.7 }}>$185/month · No contract</div>
            </Button>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', padding: '40px 0 20px' }}>
        <Container>
          <Row className="mb-4">
            <Col md={4} className="mb-3">
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px' }}>
                {isKairosIQDomain ? (
                  <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span></>
                ) : (
                  <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal</>
                )}
              </span>
              <p style={{ fontSize: '12px', color: colors.muted, marginTop: '8px', lineHeight: 1.6 }}>
                {isKairosIQDomain ? 'AI-Powered Intelligence Platform for Real Estate Professionals.' : 'AI-powered property search platform for real estate professionals. Powered by KairosIQ AI, Inc.'}
              </p>
            </Col>
            <Col md={2} className="mb-3">
              <h6 style={{ color: colors.gold, fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>Platform</h6>
              <ul className="list-unstyled" style={{ fontSize: '12px' }}>
                <li className="mb-2"><a href="/pricing" style={{ color: colors.muted, textDecoration: 'none' }}>Pricing</a></li>
                <li className="mb-2"><a href="/events" style={{ color: colors.muted, textDecoration: 'none' }}>Events</a></li>
                <li className="mb-2"><a href="/request-demo" style={{ color: colors.gold2, textDecoration: 'none' }}>Book a Demo</a></li>
                <li className="mb-2"><a href="/agent-portal/signup" style={{ color: colors.muted, textDecoration: 'none' }}>For Agents</a></li>
                <li className="mb-2"><a href="mailto:support@kairosiq.ai" style={{ color: colors.muted, textDecoration: 'none' }}>Support</a></li>
              </ul>
            </Col>
            <Col md={3} className="mb-3">
              <h6 style={{ color: colors.gold, fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>Legal & Compliance</h6>
              <ul className="list-unstyled" style={{ fontSize: '12px' }}>
                <li className="mb-2"><a href="/privacy-policy" style={{ color: colors.muted, textDecoration: 'none' }}>Privacy Policy</a></li>
                <li className="mb-2"><a href="/terms-of-service" style={{ color: colors.muted, textDecoration: 'none' }}>Terms of Service</a></li>
                <li className="mb-2"><a href="/fair-housing" style={{ color: colors.muted, textDecoration: 'none' }}>Fair Housing Statement</a></li>
                <li className="mb-2"><a href="/ai-ethics" style={{ color: colors.muted, textDecoration: 'none' }}>AI Ethics Policy</a></li>
              </ul>
            </Col>
            <Col md={3} className="mb-3 text-center">
              <h6 style={{ color: colors.gold, fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>Equal Housing</h6>
              <img
                src={require('../../assets/img/logos/equal-housing-opportunity-logo-1200w.jpg')}
                alt="Equal Housing Opportunity"
                style={{ height: '45px', objectFit: 'contain', marginBottom: '8px' }}
              />
              <p style={{ fontSize: '10px', color: colors.muted }}>
                Committed to Fair Housing and equal opportunity.
              </p>
            </Col>
          </Row>
          <hr style={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <div className="d-flex justify-content-between align-items-center">
            <p style={{ fontSize: '10px', color: colors.muted, marginBottom: 0, maxWidth: '600px' }}>
              Property data provided by participating real estate professionals and MLS organizations. All information deemed reliable but not guaranteed. AI technology powered by KairosIQ AI, Inc.
            </p>
            <p style={{ fontSize: '10px', color: colors.muted, marginBottom: 0 }}>
              © 2026 {isKairosIQDomain ? 'KairosIQ AI, Inc.' : 'AISearchPortal · KairosIQ AI, Inc.'}
            </p>
          </div>
        </Container>
      </footer>

      <style>{`
        @keyframes blink { 0%,50% { opacity: 1; } 51%,100% { opacity: 0; } }
      `}</style>
    </div>
  );
};

export default AgentPortalLanding;
